import React from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { Button } from "@/components/ui/button";
import {
  useDiscoveryInfluencer,
  ProfileBanner,
  ProfileHeroCard,
  StatsRow,
  TabsNavigation,
  OverviewTab,
  EngagementTab,
  CollaborationTab,
} from "@/domains/discovery";

export default function DiscoveryInfluencerProfile() {
  const { handle } = useParams<{ handle: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const currentTab = searchParams.get("tab") || "overview";

  const { data: influencer, isLoading, error } = useDiscoveryInfluencer(handle || "");

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 py-8">
        <LoadingSpinner size="large" label="Loading profile..." className="py-24 w-full flex justify-center" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 py-8">
        <div className="text-center py-24">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Error Loading Profile</h2>
          <p className="text-muted-foreground mb-6">
            {error instanceof Error ? error.message : "An error occurred while loading the profile."}
          </p>
          <Button onClick={() => navigate("/discovery")}>
            Back to Discovery
          </Button>
        </div>
      </div>
    );
  }

  // Not found state
  if (!influencer) {
    return (
      <div className="max-w-screen-xl mx-auto px-4 py-8">
        <div className="text-center py-24">
          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Profile Not Found</h2>
          <p className="text-muted-foreground mb-6">
            We couldn't find an influencer with the handle "@{handle}".
          </p>
          <Button onClick={() => navigate("/discovery")}>
            Back to Discovery
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="max-w-screen-xl mx-auto py-8 select-none"
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="px-4">
        <ProfileBanner name={influencer.name} onBack={() => navigate("/discovery")} />
      </div>

      <ProfileHeroCard influencer={influencer} />

      <StatsRow influencer={influencer} />

      <TabsNavigation activeTab={currentTab} setActiveTab={handleTabChange} />

      <div className="mt-6 px-6">
        {currentTab === "overview" && <OverviewTab influencer={influencer} />}
        {currentTab === "engagement" && <EngagementTab influencer={influencer} />}
        {currentTab === "collaboration" && <CollaborationTab influencer={influencer} />}
      </div>
    </div>
  );
}
